<template>
  <div
    ref="root"
    class="header-source"
    :class="{ video: page.urlVideo, playing: displayedEmbed }"
  >
    <div
      v-if="!displayedEmbed"
      class="header-source__container"
      @click="activeEmbed"
    >
      <NuxtImg
        :src="page.image"
        :alt="page.title"
        class="background-image"
        fetchpriority="high"
        sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw"
        quality="80"
        format="auto"
      />
      <img
        v-if="page.urlVideo"
        src="~/assets/icons/play.svg"
        alt="démarrer la vidéo"
        class="play"
      />
    </div>
    <iframe
      v-else
      :key="embed"
      :src="embed"
      frameborder="0"
      allowfullscreen
      allowtransparency
      allow="autoplay"
    />
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  page: {
    title?: string
    contentType?: string
    type?: string
    pegi?: string
    image?: string
    imageLegend?: string
    follow?: boolean
    favicon?: { url?: string }
    urlVideo?: string
    subject?: string
    content?: string
  }
}>()

const displayedEmbed = ref(false)
const root = ref<HTMLElement | null>(null)

// "?t=66" (a chapter link, a key moment of Google) starts the video at that second; a chapter link clicked
// in the post text (useVideoSeek) does the same, the iframe is remounted by its key when the moment changes
const route = useRoute()
const videoSeek = useVideoSeek()
const start = ref(startOffset(route.query.t))

const embed = computed(() => {
  const info = extractVideoId(props.page.urlVideo)
  const at = start.value !== undefined ? `&start=${start.value}` : ''
  const embed = `${props.page.urlVideo}?autoplay=1${at}`

  if (info?.source === 'youtube') {
    return `${embed}&iv_load_policy=3&modestbranding=1&playsinline=1&showinfo=0&rel=0`
  }

  return embed
})

const activeEmbed = () => {
  if (props.page.urlVideo) {
    displayedEmbed.value = true
  }
}

watch(videoSeek, (seek) => {
  if (!seek || !props.page.urlVideo) return
  start.value = seek.seconds
  displayedEmbed.value = true
  root.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
})

onMounted(() => {
  if (start.value !== undefined && props.page.urlVideo) displayedEmbed.value = true
})
</script>

<style lang="scss">
.header-source {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 90vh;
  max-height: 73rem;
  overflow: hidden;

  @media screen and (max-width: 430px) {
    height: 45vh;
  }

  &.playing {
    margin-bottom: -1.2rem;

    @media (min-width: 700px) {
      margin-bottom: -2.2rem;
    }
  }

  &:not(.playing)::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 2;
    width: 0;
    height: 0;
    border-width: 0 0 5rem 100vw;
    border-style: solid;
    border-color: transparent transparent $white transparent;
  }

  &.video {
    height: calc(9 / 16) * 100vw;
    max-height: none;

    .header-source__container {
      cursor: pointer;
    }
  }

  &__container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;

    &:hover .play {
      opacity: 0.75;
    }
  }

  iframe {
    z-index: 5;
    width: 100%;
    height: 100%;
  }

  .background-image {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    pointer-events: none;
    object-fit: cover;
    object-position: top;
  }

  .play {
    position: relative;
    z-index: 2;
    width: 5rem;
    height: 5rem;
    cursor: pointer;
    transition: all 0.35s cubic-bezier(0.62, 0.28, 0.23, 0.99);

    @media (min-width: 700px) {
      width: 8rem;
      height: 8rem;
    }
  }
}
</style>
